import React, { useState, useEffect } from 'react';

export default function SuggestAnswerModal({ puzzle, activeCell, onClose }) {
  const [name,       setName]       = useState('');
  const [note,       setNote]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState('');

  // Honeypot field — hidden from users, filled by bots
  const [_hp, setHp] = useState('');

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const rowLabel = activeCell
    ? puzzle.row_labels[activeCell.r]?.label || `Row ${activeCell.r + 1}`
    : null;
  const colLabel = activeCell
    ? puzzle.col_labels[activeCell.c]?.label || `Col ${activeCell.c + 1}`
    : null;

  const handleSubmit = async () => {
    if (!name.trim() || name.trim().length < 2) {
      setError('Please enter a player name.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/suggest-answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          puzzleId:      puzzle.id,
          puzzleTitle:   puzzle.title,
          rowLabel:      rowLabel || '',
          colLabel:      colLabel || '',
          suggestedName: name.trim(),
          note:          note.trim() || null,
          _hp,           // honeypot
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Submission failed');
      }
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-surface2 border border-border rounded-2xl w-full max-w-sm shadow-2xl"
        style={{ animation: 'popIn 0.2s ease-out' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-border">
          <div>
            <div className="font-semibold text-white">Suggest a missing answer</div>
            <div className="text-xs text-dimText mt-0.5">
              Think we missed a valid player? Let us know.
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full border border-border text-dimText hover:border-accent hover:text-white transition-colors text-xs"
          >✕</button>
        </div>

        {!submitted ? (
          <div className="px-5 py-4 space-y-3">
            {/* Cell context */}
            {rowLabel && colLabel && (
              <div className="bg-surface border border-border rounded-xl px-3 py-2.5 text-xs text-dimText">
                <span className="text-accent font-semibold">{colLabel}</span>
                <span className="mx-1.5 opacity-50">×</span>
                <span className="text-accent2 font-semibold">{rowLabel}</span>
              </div>
            )}

            {/* Player name */}
            <div>
              <label className="text-xs text-dimText mb-1.5 block">Player name <span className="text-wrong">*</span></label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                placeholder="e.g. Raúl González"
                maxLength={60}
                autoFocus
                className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-dimText outline-none focus:border-accent transition-colors"
              />
            </div>

            {/* Optional note */}
            <div>
              <label className="text-xs text-dimText mb-1.5 block">Why do you think they qualify? <span className="opacity-50">(optional)</span></label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="e.g. Played for Real Madrid 1994–2002, won La Liga multiple times"
                rows={2}
                maxLength={200}
                className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-dimText outline-none focus:border-accent transition-colors resize-none"
              />
            </div>

            {/* Honeypot — hidden from real users */}
            <input
              type="text"
              value={_hp}
              onChange={e => setHp(e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
            />

            {error && <p className="text-wrong text-xs">{error}</p>}

            <div className="flex gap-2 pt-1">
              <button
                onClick={handleSubmit}
                disabled={submitting || !name.trim()}
                className="flex-1 bg-accent text-bg text-sm font-semibold py-2.5 rounded-xl hover:bg-accent/90 disabled:opacity-40 transition-colors"
              >
                {submitting ? 'Sending…' : 'Submit suggestion'}
              </button>
              <button
                onClick={onClose}
                className="flex-1 border border-border text-dimText text-sm py-2.5 rounded-xl hover:border-accent hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-8 text-center">
            <div className="text-4xl mb-3">✅</div>
            <div className="font-semibold text-white mb-1">Thanks for the suggestion!</div>
            <div className="text-sm text-dimText mb-5">We'll review it and add the player if they qualify.</div>
            <button
              onClick={onClose}
              className="bg-accent text-bg text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-accent/90 transition-colors"
            >
              Back to game
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
